import React, { useState, useEffect } from 'react';
import { businessMetrics } from '../../config/businessMetrics';
import { ContextualCTA } from '../CTA';

export default function TeamSection() {
  const [flippedCard, setFlippedCard] = useState(null);
  const [hoveredCard, setHoveredCard] = useState(null);
  const [selectedMember, setSelectedMember] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  const [counters, setCounters] = useState({ engagements: 0, years: 0, exits: 0, value: 0 });

  const teamMembers = [
    {
      id: 1,
      name: 'Michael A. Pantouvakis',
      role: 'Founder & Managing Principal',
      image: '/mike-pantouvakis.jpg',
      initials: 'MP',
      color: 'from-amiva-primary to-blue-600',
      bio: 'NYU Stern founder who built and sold ventures before launching AMIVA\'s tech-enabled sell-side platform.',
      longBio: 'Michael founded AMIVA Equity Partners after a decade of building and advising growth companies. He launched his first venture, PowerLiving, at 14 and grew it to a 60,000-person weekly audience across 30 countries. He later created Vim, a smart fashion brand acquired in 2017, and the apparel collective Bluntel. Between ventures he advised institutional clients on Wall Street and at a leading European shipping bank while earning his business degree from NYU Stern. At AMIVA, Michael combines entrepreneurial instincts with capital markets discipline to help lower-middle-market founders engineer competitive processes, using data-driven buyer sourcing to unlock premium valuations.',
      highlights: ['Founded PowerLiving at 14', 'Built and sold Vim (2017)', 'NYU Stern finance graduate', 'Wall Street & European banking experience', 'Launched tech-enabled AMIVA platform'],
      linkedin: 'https://www.linkedin.com/in/mikepantouvakis',
      email: 'mike@aepacquisition.com',
      calendly: 'https://app.usemotion.com/meet/mike-pantouvakis/acquisition-discussion',
      achievements: ['Scaled ventures across three continents', 'Grew Vim through acquisition', 'Built AMIVA\'s buyer intelligence engine'],
      specializations: ['Sell-Side Strategy', 'Landscape & Facility Services', 'Healthcare', 'Tech-Enabled Services', 'Cross-Border Outreach'],
      education: [
        { degree: 'B.S. Business', school: 'NYU Stern School of Business', focus: 'Finance & Entrepreneurship' }
      ],
      funFact: 'Launched his first brand before he could drive.',
      quote: 'Pair entrepreneurial empathy with rigorous process and buyers line up.',
      successStory: 'Guided a specialty services founder through a tech-enabled buyer sprint that delivered multiple strategic offers within weeks.'
    },
    {
      id: 2,
      name: 'Matthew Hernandez',
      role: 'Principal Dental M&A Advisor',
      initials: 'MH',
      color: 'from-amiva-accent to-emerald-600',
      bio: 'Principal dental advisor who has launched 35+ practices, five labs, and now scales AMIVA\'s dental roll-up playbooks.',
      longBio: 'Matthew leads AMIVA\'s dental advisory practice with more than two decades of multi-site operational experience. A third-generation dental operator, he has opened and developed 35 practices and five laboratories across Texas and Washington, architected remote KPI programs that lifted EBITDA by 50%+, and guided consolidation strategies for leading DSOs and MSOs. His background spans business development, clinical training, and tribal healthcare initiatives, giving AMIVA clients a partner who understands both chairside excellence and disciplined back-office execution.',
      highlights: ['Leads AMIVA dental advisory practice', '35+ dental practices launched', '5 laboratories developed', '20+ years multi-site operations', 'Architect of KPI-driven remote management'],
      linkedin: 'https://www.linkedin.com/in/matthewhernandez',
      email: 'matthew@aepacquisition.com',
      achievements: ['Scaled multi-site dental networks to $40M+ assets', 'Lifted EBITDA 50%+ through KPI programs', 'Built mobile care reach serving 5,800 residents'],
      specializations: ['Dental M&A', 'DSO/MSO Operations', 'Remote Practice Management', 'Turnaround Programs', 'Strategic Exits'],
      education: [
        { degree: 'Dental Operations Training', school: 'Indian Health Services Collaboration', focus: 'Tribal Clinician Program' },
        { degree: 'Dental Technology Certification', school: 'George Brown College', focus: 'Dental Health' },
        { degree: 'Denturist Program', school: 'Bates Technical College', focus: 'Advanced Removable Prosthetics' }
      ],
      certifications: ['Executive Practice Coach', 'DSO Operations Expert'],
      funFact: 'Third-generation dental operator who started managing practices at 16.',
      quote: 'Systems create freedom—both for patients and for practice owners.',
      successStory: 'Partnered with an AMIVA dental client to consolidate operations and marketing, lifting EBITDA from single digits to 50% in under four years.'
    },
    {
      id: 3,
      name: 'Dr. Hendrik Lai',
      role: 'Physician-Led Industry Expert',
      initials: 'HL',
      color: 'from-amiva-slate to-slate-600',
      bio: 'Dentist, multi-practice owner, and PE advisor with MBA, EMBA, and MS credentials plus fellowships across three institutes.',
      longBio: 'Dr. Hendrik Lai helps physicians balance care delivery with scalable business performance. A practicing dentist and multi-practice owner, he has advised private equity groups, global consultancies, and healthcare operators on growth strategy, platform integration, and quality systems. Hendrik holds a dental degree from the University of Adelaide along with MBA, EMBA, and MS credentials spanning business analytics and organizational leadership. He is a Chartered Management Consultant, Certified Manager, Certified QMS Auditor, and Fellow of the Institute of Managers and Leaders, International Institute of Directors and Managers, and the Institute of Leadership and Management. His perspectives have been featured by outlets such as SMH, ABC, and CBS.',
      highlights: ['University of Adelaide dentist', 'MBA + EMBA + MS credentials', 'Chartered Management Consultant', 'Certified QMS Auditor', 'Featured by SMH/ABC/CBS'],
      linkedin: 'https://www.linkedin.com/in/hendrik-lai',
      email: 'hendrik@aepacquisition.com',
      achievements: ['Scaled physician-led roll-ups', 'Implemented ISO-aligned quality systems', 'Advised global consulting firms'],
      specializations: ['Healthcare M&A', 'Physician-Led Roll-ups', 'Quality Management Systems', 'Strategic Consulting', 'PE Advisory'],
      education: [
        { degree: 'BDS', school: 'University of Adelaide', focus: 'Dentistry' },
        { degree: 'MBA & EMBA', school: 'Global Business Programs', focus: 'Strategy & Leadership' },
        { degree: 'MS', school: 'Graduate Studies', focus: 'Management Systems & Analytics' }
      ],
      certifications: ['Certified QMS Auditor', 'Chartered Management Consultant', 'Certified Manager', 'Fellow - IML', 'Fellow - IIDM', 'Fellow - ILM'],
      funFact: 'Named Fellow by three international leadership institutes.',
      quote: 'Clinical empathy goes further when coupled with disciplined operating systems.',
      successStory: 'Partnered with a physician-led group to align KPIs and quality protocols, accelerating platform growth while preserving patient experience.'
    },
    {
      id: 4,
      name: 'Tim Rayer',
      role: 'Facility Management Expert',
      initials: 'TR',
      color: 'from-amber-500 to-orange-500',
      bio: '30-year building systems leader across The Pharm, Climatec, Johnson Controls, and national commissioning firms.',
      longBio: 'Tim guides AMIVA\'s industrial and facility services clients with more than three decades of HVAC, building automation, and energy efficiency expertise. He has run regional mechanical outfits, managed large-scale controls projects for national integrators, and led commissioning teams serving universities, hospitals, and industrial campuses. Tim translates field-level rigor into strategic narratives, uncovering operational upside that resonates with strategic and private equity buyers.',
      highlights: ['Leads AMIVA industrial services practice', '30+ years HVAC and controls expertise', 'Directed large-scale automation projects', 'Commissioning authority for complex campuses', 'Energy efficiency strategist'],
      email: 'tim@aepacquisition.com',
      linkedin: 'https://www.linkedin.com/in/tim-rayer-26715073',
      achievements: ['Directed multi-state building automation projects', 'Delivered commissioning programs for universities and hospitals', 'Identified post-close efficiency wins for industrial buyers'],
      specializations: ['HVAC Systems', 'Building Automation', 'Commissioning', 'Energy Efficiency', 'Industrial & Facility Services'],
      education: [
        { degree: 'Applied Engineering Training', school: 'Industry Programs', focus: 'Building Automation & Controls' }
      ],
      funFact: 'Prefers to start facility walkthroughs on the roof to assess airflow first.',
      quote: 'Operational discipline drives believable EBITDA expansion.',
      successStory: 'Mapped a phased controls upgrade for a family-owned HVAC platform that substantiated a double-digit valuation premium.'
    },
    {
      id: 5,
      name: 'Khris Andrews',
      role: 'Executive Assistant',
      initials: 'KA',
      color: 'from-amiva-primary to-pink-600',
      bio: 'Heads AMIVA\'s executive operations office, keeping diligence, CRM, and client communications running on rails.',
      longBio: 'Khris Andrews leads AMIVA\'s executive operations and client experience function. She designs the inbox, calendar, and workflow systems that keep deal teams on schedule, implements SOPs that maintain white-glove communication with founders, and orchestrates complex project timelines across valuation, marketing, and diligence streams. Recognized nationally for her work in virtual executive support, Khris brings both empathy and operational precision to every engagement.',
      highlights: ['Leads AMIVA executive operations', 'Alignable Local Business Person of the Year 2025', 'Scaled remote support programs', 'Workflow & SOP architect', 'Trusted partner to high-growth founders'],
      linkedin: 'https://www.linkedin.com/in/khrisandrews',
      achievements: ['Implemented end-to-end CRM and calendar systems', 'Maintains 24-hour client response loops', 'Recognized community business leader'],
      specializations: ['Executive Operations', 'Virtual Support Systems', 'Inbox Automation', 'Priority Management', 'Project Coordination'],
      education: [
        { degree: 'Business Operations', school: 'Professional Experience', focus: 'Executive Support & Systems Design' }
      ],
      funFact: 'Runs AMIVA\'s operations hub from the Appalachian mountains while supporting teams coast-to-coast.',
      quote: 'Operational clarity is what lets founders stay focused on the next decision.',
      successStory: 'Built a 24-hour response workflow for AMIVA\'s deal team, syncing CRM, calendaring, and client updates without adding headcount.'
    },
    {
      id: 6,
      name: 'Kris Ng',
      role: 'Head of Financials',
      initials: 'KN',
      color: 'from-blue-500 to-cyan-500',
      bio: 'Singapore-based associate leading research, valuation modeling, and Asia-to-U.S. buyer outreach.',
      longBio: 'Based in Singapore, Kris supports AMIVA\'s financial modeling, competitive research, and global buyer development. He has sourced international interest for mandates across healthcare and technology services, building dossiers that help U.S. founders engage capital from Asia-Pacific while maintaining momentum with domestic buyers. Kris blends analytical rigor with cultural fluency, translating market dynamics into actionable positioning for investors.',
      highlights: ['Leads AMIVA cross-border buyer sourcing', 'Financial modeling specialist', 'Market & competitor researcher'],
      achievements: ['Secured Asia-based interest for U.S. mandates', 'Delivered valuation support materials', 'Improved investor targeting data'],
      specializations: ['Valuation Modeling', 'Buyer Outreach', 'Healthcare & Technology Services', 'Cross-Border Research', 'Market Mapping'],
      education: [
        { degree: 'International Business Studies', school: 'Singapore-Based Programs', focus: 'Finance & Markets' }
      ],
      funFact: 'Keeps a running map of buyer conversations across three time zones.',
      quote: 'Great deals start with facts that buyers can trust.',
      successStory: 'Built a cross-border investor list that expanded competition for a specialty healthcare mandate beyond North America.'
    },
    // NOTE: Muzaffar Z. Fatani (Head of Growth) is intentionally not listed publicly.
    // His full profile is preserved in ./archivedTeamMembers.js (not imported, so it is
    // never shipped to the browser). Re-add the record here to restore him to the site.
  ];

  // Animated counter effect
  useEffect(() => {
    const timer = setTimeout(() => {
      const interval = setInterval(() => {
        setCounters(prev => ({
          engagements: prev.engagements < businessMetrics.deals.totalCompleted ? prev.engagements + 1 : businessMetrics.deals.totalCompleted,
          years: prev.years < businessMetrics.team.experience.collectiveYears ? prev.years + 1 : businessMetrics.team.experience.collectiveYears,
          exits: prev.exits < businessMetrics.deals.exits ? prev.exits + 1 : businessMetrics.deals.exits,
          value: prev.value < businessMetrics.deals.totalValue ? prev.value + 10 : businessMetrics.deals.totalValue,
        }));
      }, 50);
      
      setTimeout(() => clearInterval(interval), 2600);
    }, 500);
    
    return () => clearTimeout(timer);
  }, []);

  const cultureHighlights = [
    {
      title: 'Founder-First Outcomes',
      description: 'Everything we build is in service of creating better exits for lower middle-market founders.'
    },
    {
      title: 'Remote, High-Cadence Collaboration',
      description: 'We operate across time zones and expect crisp communication, clear ownership, and ship velocity.'
    },
    {
      title: 'Human + AI Operating System',
      description: 'We pair expert judgement with automation so our deals move faster and with more precision.'
    }
  ];

  const jobOpenings = [
    {
      title: 'Associate, Sell-Side Advisory',
      type: 'Full-time',
      location: 'New York, NY — On-site',
      description: 'Own workstreams across market mapping, buyer outreach, and diligence alongside our NYC deal team to keep momentum high.',
      applyLink:
        'mailto:careers@amivaequitypartners.com?subject=Associate%20Sell-Side%20Advisory%20Application&body=Hi%20AMIVA%20Talent%20Team%2C%0A%0AI%27m%20interested%20in%20the%20Associate%2C%20Sell-Side%20Advisory%20role.%0A%0AWhy%20I%27m%20a%20fit%3A%0A-%20%5BKey%20experience%201%5D%0A-%20%5BKey%20experience%202%5D%0A%0ARecent%20wins%3A%0A-%20%5BWin%201%5D%0A-%20%5BWin%202%5D%0A%0AAvailability%3A%20%5BTimezone%20overlap%2C%20start%20date%5D%0AAttachments%3A%20%5BResume%2FDeck%5D%0A%0AThank%20you%2C%0A%5BYour%20Name%5D%0A%5BLinkedIn%20or%20Portfolio%5D'
    },
    {
      title: 'AI Platform Engineer',
      type: 'Full-time',
      location: 'Remote — North America or LATAM',
      description: 'Build and deploy AI-assisted tooling that powers our deal platform, from data pipelines to workflow automations.',
      applyLink:
        'mailto:careers@amivaequitypartners.com?subject=AI%20Platform%20Engineer%20Application&body=Hi%20AMIVA%20Talent%20Team%2C%0A%0AI%27m%20interested%20in%20the%20AI%20Platform%20Engineer%20role.%0A%0AWhat%20I%27ve%20built%3A%0A-%20%5BRecent%20AI%20project%5D%0A-%20%5BRelevant%20systems%5D%0A%0ADaily%20tools%3A%20%5BLanguages%2C%20frameworks%5D%0AAvailability%3A%20%5BTimezone%2C%20start%20date%5D%0A%0ALinks%3A%20%5BGitHub%20or%20Portfolio%5D%0A%0AThanks%2C%0A%5BYour%20Name%5D'
    }
  ];

  return (
    <div className="bg-white">
      {/* Enhanced Hero Section with Animation */}
      <div className="relative z-0 bg-gradient-to-br from-gray-900 via-gray-800 to-black py-24 lg:py-32 overflow-hidden">
        {/* Animated Background Pattern */}
        <div className="absolute inset-0 pointer-events-none z-0">
          <div className="absolute top-20 left-20 w-96 h-96 bg-amiva-primary rounded-full blur-3xl opacity-20 animate-pulse"></div>
          <div className="absolute bottom-20 right-20 w-96 h-96 bg-amiva-accent rounded-full blur-3xl opacity-20 animate-pulse" style={{ animationDelay: '1s' }}></div>
          <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-gradient-to-r from-amiva-primary/10 to-amiva-accent/10 rounded-full blur-3xl"></div>
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="mb-8 animate-fade-in-down">
            <span className="bg-white/10 backdrop-blur-sm text-white text-sm font-semibold px-5 py-2.5 rounded-full inline-flex items-center shadow-lg border border-white/20">
              <span className="w-2 h-2 bg-amiva-accent rounded-full mr-2.5 animate-pulse"></span>
              Meet the Team
            </span>
          </div>
          <h1 className="text-5xl lg:text-7xl font-bold text-white mb-6 leading-tight animate-fade-in">
            The Team That <span className="text-gradient bg-gradient-to-r from-amiva-primary to-amiva-accent bg-clip-text text-transparent">Gets It</span>
          </h1>
          <p className="text-xl lg:text-2xl text-gray-200 max-w-4xl mx-auto leading-relaxed mb-12 animate-fade-in-up">
            We're not just advisors. We're entrepreneurs who have built, scaled, and successfully
            exited businesses ourselves.
          </p>
          
          {/* Team Stats Counter */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 max-w-4xl mx-auto mt-16">
            <div className="text-center animate-fade-in" style={{ animationDelay: '0.2s' }}>
              <div className="text-4xl md:text-5xl font-bold text-amiva-primary mb-2">{counters.engagements}+</div>
              <div className="text-sm text-gray-300 uppercase tracking-wider">Successful Engagements</div>
            </div>
            <div className="text-center animate-fade-in" style={{ animationDelay: '0.3s' }}>
              <div className="text-4xl md:text-5xl font-bold text-amiva-accent mb-2">{counters.years}+</div>
              <div className="text-sm text-gray-300 uppercase tracking-wider">Years Combined</div>
            </div>
            <div className="text-center animate-fade-in" style={{ animationDelay: '0.4s' }}>
              <div className="text-4xl md:text-5xl font-bold text-blue-500 mb-2">{counters.exits}</div>
              <div className="text-sm text-gray-300 uppercase tracking-wider">Successful Exits</div>
            </div>
            <div className="text-center animate-fade-in" style={{ animationDelay: '0.5s' }}>
              <div className="text-4xl md:text-5xl font-bold text-emerald-500 mb-2">${counters.value}M+</div>
              <div className="text-sm text-gray-300 uppercase tracking-wider">Transaction Value</div>
            </div>
          </div>
        </div>
      </div>

      {/* Enhanced Team Members Section with New Design */}
      <div className="relative z-10 py-24 lg:py-32 bg-gradient-to-b from-white via-gray-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Section Header */}
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              Leadership & <span className="text-gradient bg-gradient-to-r from-amiva-primary to-amiva-accent bg-clip-text text-transparent">Expertise</span>
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Meet the experienced professionals who will guide your exit strategy
            </p>
          </div>

          {/* Add custom styles for animations and effects */}
          <style>{`
            .team-card {
              transition: all 0.5s cubic-bezier(0.4, 0, 0.2, 1);
            }
            .team-card:hover {
              transform: translateY(-10px);
            }
            .profile-ring {
              background: linear-gradient(135deg, #4F46E5 0%, #10B981 100%);
              padding: 4px;
              border-radius: 50%;
            }
            .badge-slide-in {
              animation: slideIn 0.3s ease-out forwards;
            }
            @keyframes slideIn {
              from {
                opacity: 0;
                transform: translateX(-10px);
              }
              to {
                opacity: 1;
                transform: translateX(0);
              }
            }
            .animate-fade-in {
              animation: fadeIn 0.6s ease-out;
            }
            .animate-fade-in-up {
              animation: fadeInUp 0.6s ease-out;
            }
            .animate-fade-in-down {
              animation: fadeInDown 0.6s ease-out;
            }
            @keyframes fadeIn {
              from { opacity: 0; }
              to { opacity: 1; }
            }
            @keyframes fadeInUp {
              from {
                opacity: 0;
                transform: translateY(20px);
              }
              to {
                opacity: 1;
                transform: translateY(0);
              }
            }
            @keyframes fadeInDown {
              from {
                opacity: 0;
                transform: translateY(-20px);
              }
              to {
                opacity: 1;
                transform: translateY(0);
              }
            }
          `}</style>

          <div className="relative z-20 isolate grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {teamMembers.map((member, index) => (
              <div
                key={member.id}
                className="team-card group relative animate-fade-in-up h-full"
                style={{ animationDelay: `${index * 0.1}s` }}
                onMouseEnter={() => setHoveredCard(member.id)}
                onMouseLeave={() => setHoveredCard(null)}
              >
                <div className="bg-white rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-500 overflow-hidden border border-gray-100 flex flex-col h-full">
                  {/* Top Gradient Bar */}
                  <div className={`h-2 bg-gradient-to-r ${member.color}`}></div>
                  
                  {/* Card Content */}
                  <div className="p-6 relative z-10 flex flex-col flex-1">
                    {/* Profile Section */}
                    <div className="relative mb-6">
                      <div className="profile-ring mx-auto w-32 h-32">
                        <div className="bg-white rounded-full w-full h-full overflow-hidden">
                          <div className={`w-full h-full bg-gradient-to-br ${member.color} flex items-center justify-center`}>
                            {member.image && member.id === 1 ? (
                              <>
                                <img
                                  src={member.image}
                                  alt={member.name}
                                  className="w-full h-full object-cover"
                                  onError={(e) => {
                                    e.target.onerror = null;
                                    e.target.style.display = 'none';
                                    e.target.nextSibling.style.display = 'flex';
                                  }}
                                />
                                <span className="text-white font-bold text-3xl hidden items-center justify-center w-full h-full">
                                  {member.initials}
                                </span>
                              </>
                            ) : (
                              <span className="text-white font-bold text-3xl">{member.initials}</span>
                            )}
                          </div>
                        </div>
                      </div>
                      
                      {/* Status Indicator */}
                      <div className="absolute bottom-0 right-1/2 transform translate-x-1/2 translate-y-2 pointer-events-none">
                        <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                          <span className="w-2 h-2 bg-green-500 rounded-full mr-1.5 animate-pulse"></span>
                          Available
                        </span>
                      </div>
                    </div>
                    
                    {/* Name and Role */}
                    <div className="text-center mb-4">
                      <h3 className="text-xl font-bold text-gray-900 mb-1">{member.name}</h3>
                      <p className="text-sm text-gray-600 font-medium">{member.role}</p>
                    </div>
                    
                    {/* Specializations Pills */}
                    <div className="flex flex-wrap gap-2 justify-center mb-4">
                      {member.specializations.slice(0, 2).map((specialization, idx) => (
                        <span
                          key={idx}
                          className={`${hoveredCard === member.id ? 'badge-slide-in' : ''} bg-gray-100 text-gray-700 px-3 py-1 rounded-full text-xs font-semibold`}
                          style={{ animationDelay: `${idx * 0.1}s` }}
                        >
                          {specialization}
                        </span>
                      ))}
                    </div>
                    
                    {/* Quote */}
                    <p className="text-gray-600 text-sm italic text-center mb-6 line-clamp-2">
                      "{member.quote}"
                    </p>
                    
                    {/* Action Buttons */}
                    <div className="mt-auto pt-4">
                      <button
                        onClick={() => setSelectedMember(member)}
                        className={`w-full bg-gradient-to-r ${member.color} text-white py-2.5 rounded-lg font-semibold hover:shadow-lg transition-all transform hover:scale-105 text-sm cursor-pointer`}
                        type="button"
                      >
                        View Full Profile
                      </button>
                    </div>
                    
                    {/* Hover Effect Specializations */}
                    <div className={`mt-4 transition-all duration-300 ${hoveredCard === member.id ? 'max-h-24 opacity-100' : 'max-h-0 opacity-0 overflow-hidden'}`}>
                      <div className="pt-4 border-t border-gray-200">
                        <p className="text-xs font-semibold text-gray-500 mb-2">SPECIALIZES IN:</p>
                        <div className="flex flex-wrap gap-1">
                          {member.specializations.slice(0, 3).map((spec, idx) => (
                            <span key={idx} className="text-xs text-gray-600">
                              {spec}{idx < 2 ? ',' : ''}
                            </span>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Member Detail Modal */}
      {selectedMember && (
        <div className="fixed inset-0 z-50 overflow-y-auto bg-black/50 backdrop-blur-sm" onClick={() => setSelectedMember(null)}>
          <div className="flex min-h-screen items-center justify-center p-4">
            <div 
              className="relative w-full max-w-4xl bg-white rounded-2xl shadow-2xl overflow-hidden animate-fade-in-up"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Modal Header with Gradient */}
              <div className={`bg-gradient-to-r ${selectedMember.color} p-8 text-white relative`}>
                <button
                  onClick={() => setSelectedMember(null)}
                  className="absolute top-4 right-4 text-white/80 hover:text-white transition-colors"
                >
                  <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
                
                <div className="flex items-center gap-6">
                  <div className="w-24 h-24 bg-white/20 backdrop-blur-sm rounded-full p-1">
                    <div className={`w-full h-full bg-gradient-to-br ${selectedMember.color} rounded-full flex items-center justify-center`}>
                      {selectedMember.image && selectedMember.id === 1 ? (
                        <img src={selectedMember.image} alt={selectedMember.name} className="w-full h-full object-cover rounded-full" />
                      ) : (
                        <span className="text-white font-bold text-2xl">{selectedMember.initials}</span>
                      )}
                    </div>
                  </div>
                  <div>
                    <h2 className="text-3xl font-bold mb-1">{selectedMember.name}</h2>
                    <p className="text-white/90 text-lg">{selectedMember.role}</p>
                  </div>
                </div>
              </div>
              
              {/* Modal Content with Tabs */}
              <div className="p-8">
                {/* Tab Navigation */}
                <div className="flex gap-4 border-b border-gray-200 mb-6">
                  {['overview', 'experience'].map((tab) => (
                    <button
                      key={tab}
                      onClick={() => setActiveTab(tab)}
                      className={`pb-3 px-1 text-sm font-semibold capitalize transition-colors relative ${
                        activeTab === tab ? 'text-amiva-primary' : 'text-gray-500 hover:text-gray-700'
                      }`}
                    >
                      {tab}
                      {activeTab === tab && (
                        <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-amiva-primary"></div>
                      )}
                    </button>
                  ))}
                </div>
                
                {/* Tab Content */}
                <div className="min-h-[300px]">
                  {activeTab === 'overview' && (
                    <div className="space-y-6 animate-fade-in">
                      <div>
                        <h3 className="text-lg font-bold text-gray-900 mb-3">About</h3>
                        <p className="text-gray-600 leading-relaxed">{selectedMember.longBio}</p>
                      </div>
                      
                      <div>
                        <h3 className="text-lg font-bold text-gray-900 mb-3">Success Story</h3>
                        <div className="bg-gradient-to-br from-gray-50 to-white p-4 rounded-xl border border-gray-200">
                          <p className="text-gray-600 italic">{selectedMember.successStory}</p>
                        </div>
                      </div>
                      
                      <div>
                        <h3 className="text-lg font-bold text-gray-900 mb-3">Specializations</h3>
                        <div className="grid grid-cols-3 gap-4">
                          {selectedMember.specializations.map((specialization, idx) => (
                            <div key={idx} className="bg-gray-50 rounded-lg p-3 text-center">
                              <span className="text-sm font-semibold text-gray-700">{specialization}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}
                  
                  {activeTab === 'experience' && (
                    <div className="space-y-6 animate-fade-in">
                      <div>
                        <h3 className="text-lg font-bold text-gray-900 mb-3">Areas of Expertise</h3>
                        <div className="grid grid-cols-2 gap-3">
                          {selectedMember.specializations.map((spec, idx) => (
                            <div key={idx} className="flex items-center gap-2">
                              <svg className="w-5 h-5 text-amiva-primary" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                              </svg>
                              <span className="text-gray-700">{spec}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                      
                      <div>
                        <h3 className="text-lg font-bold text-gray-900 mb-3">Professional Highlights</h3>
                        <ul className="space-y-2">
                          {selectedMember.highlights.map((highlight, idx) => (
                            <li key={idx} className="flex items-start gap-2">
                              <span className="text-amiva-primary mt-1">•</span>
                              <span className="text-gray-600">{highlight}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                      
                      {selectedMember.certifications && (
                        <div>
                          <h3 className="text-lg font-bold text-gray-900 mb-3">Certifications</h3>
                          <div className="flex flex-wrap gap-2">
                            {selectedMember.certifications.map((cert, idx) => (
                              <span key={idx} className="bg-amiva-primary/10 text-amiva-primary px-3 py-1 rounded-full text-sm font-medium">
                                {cert}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Careers Section */}
      <div className="py-24 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">
            <div className="animate-fade-in-up">
              <span className="inline-flex items-center text-sm font-semibold text-amiva-primary bg-amiva-primary/10 rounded-full px-4 py-2 mb-6">
                <span className="w-2 h-2 bg-amiva-primary rounded-full mr-2 animate-pulse"></span>
                We&apos;re Hiring
              </span>
              <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6 leading-tight">
                Join the Builders Behind AMIVA Equity Partners
              </h2>
              <p className="text-lg text-gray-600 mb-8">
                We are assembling a remote-first team of operators, analysts, and storytellers who know how to ship fast, think like owners, and guide founders through once-in-a-lifetime exits. If you move quickly, love data, and thrive in ambiguity, we want to hear from you.
              </p>

              <div className="space-y-6">
                {cultureHighlights.map((highlight, index) => (
                  <div key={highlight.title} className="flex items-start gap-4">
                    <div className="w-10 h-10 rounded-full bg-gradient-to-br from-amiva-primary to-amiva-accent text-white flex items-center justify-center font-semibold shadow-lg">
                      {`0${index + 1}`}
                    </div>
                    <div>
                      <h3 className="text-xl font-semibold text-gray-900 mb-1">{highlight.title}</h3>
                      <p className="text-gray-600">{highlight.description}</p>
                    </div>
                  </div>
                ))}
              </div>

              <div className="mt-10 p-6 rounded-2xl border border-dashed border-amiva-primary/30 bg-amiva-primary/5">
                <h3 className="text-lg font-semibold text-gray-900 mb-2">How to Apply</h3>
                <p className="text-gray-600">
                  Send a short note and your latest resume or portfolio to{' '}
                  <a
                    className="text-amiva-primary font-semibold hover:underline"
                    href="mailto:careers@amivaequitypartners.com"
                  >
                    careers@amivaequitypartners.com
                  </a>{' '}
                  with the role you&apos;re pursuing in the subject line. Include the hardest problem you&apos;ve solved in the past year—we read every application.
                </p>
              </div>
            </div>

            <div className="animate-fade-in-up" style={{ animationDelay: '0.1s' }}>
              <div className="bg-white rounded-3xl shadow-2xl border border-gray-100 overflow-hidden">
                <div className="bg-gradient-to-r from-amiva-primary to-amiva-accent text-white px-8 py-6">
                  <h3 className="text-2xl font-semibold text-white">Open Roles</h3>
                  <p className="text-sm text-white/75 mt-1">We review candidates on a rolling basis.</p>
                </div>

                <div className="divide-y divide-gray-100">
                  {jobOpenings.map(role => (
                    <div key={role.title} className="p-8">
                      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
                        <h4 className="text-xl font-semibold text-gray-900">{role.title}</h4>
                        <span className="inline-flex items-center bg-gray-100 text-gray-700 text-xs font-medium uppercase tracking-wide px-3 py-1 rounded-full">
                          {role.type}
                        </span>
                      </div>
                      <div className="flex flex-wrap items-center gap-3 text-sm text-gray-500 mb-4">
                        <div className="inline-flex items-center gap-2">
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4l2.5 1.5" />
                            <circle cx="12" cy="12" r="9" />
                          </svg>
                          {role.location}
                        </div>
                      </div>
                      <p className="text-gray-600 mb-6">{role.description}</p>
                      <a
                        href={role.applyLink}
                        className="inline-flex items-center justify-center w-full bg-gradient-to-r from-amiva-primary to-amiva-accent text-white py-2.5 rounded-lg font-semibold hover:shadow-lg transition-all transform hover:scale-105 text-sm"
                      >
                        Apply Now
                        <svg className="w-4 h-4 ml-2" fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M17 8l4 4m0 0l-4 4m4-4H3" />
                        </svg>
                      </a>
                    </div>
                  ))}
                </div>
              </div>

              <div className="mt-6 text-center text-sm text-gray-500">
                Don&apos;t see the right fit? Reach out anyway—we create roles for exceptional people.
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* CTA Section - Contextual variant for team page */}
      <ContextualCTA variant="about" />
    </div>
  );
}
